import React, { useState, useEffect, FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../common/contexts/UserContext";
import { useUserPreferences } from "../../common/contexts/UserPreferencesContext";
import ComponentWrapper from "../../common/components/ComponentWrapper";
import Button from "@murphy-frontend/web-core/components/Button";
import { TemplateCreatorList } from "@murphy-frontend/web-core/columns";
import { ColumnHeaderCell, ColumnRowCell } from "@murphy-frontend/web-core/components/Table/Models";
import Table from "@murphy-frontend/web-core/components/Table";
import { useGetTemplates as useGetEventFlowTemplates } from "./apiEventflow/queries";
import { useCustomer } from "../../common/contexts/CustomerContext";
import { useDeleteIncidentTemplate } from "./apiEventflow/mutations";
import { getAlertTemplates2 } from "./apiAlert/queries";
import { useDeleteAlertTemplate } from "./apiAlert/mutations"; // Assume this exists
import Spinner from "@murphy-frontend/web-core/components/Spinner";
import ActionsRow from "./components/ActionsRow";

const TemplateCreaterList: FunctionComponent = () => {
    const navigate = useNavigate();
    const { customer } = useCustomer();
    const [templateIncidentTableData, setTemplateIncidentTableData] = useState<ColumnRowCell[][]>([]);
    const [templateAlertTableData, setTemplateAlertTableData] = useState<ColumnRowCell[][]>([]);
    const [templateCreatorColumns, setTemplateCreatorColumns] = useState<ColumnHeaderCell[]>([]);
    const { user } = useUser();
    const { data: templates, isLoading: templatesIsLoading } = useGetEventFlowTemplates(customer?.Id);
    const { mutate: deleteIncidentTemplate, isPending: deleteIncidentIsLoading } = useDeleteIncidentTemplate(customer?.Id);
    const { data: alertTemplates, isLoading: alertTemplatesIsLoading } = getAlertTemplates2(customer?.Id);
    const { mutate: deleteAlertTemplate, isPending: deleteAlertIsLoading } = useDeleteAlertTemplate(); 

    const { translations } = useUserPreferences();

    useEffect(() => {
        if (translations && templates && templates.length > 0) {
            setTemplateCreatorColumns(TemplateCreatorList(translations));

            const templateRowCell: ColumnRowCell[][] = templates.map((template, idx) => [
                {
                    id: template.Id,
                    rowId: idx,
                    displayValue: template.Name,
                    value: template.Name,
                    columnKey: "name"
                },
                {
                    id: template.Id,
                    rowId: idx,
                    displayValue: template.Description,
                    value: template.Description,
                    columnKey: "description"
                },
                {
                    id: template.Id,
                    rowId: idx,
                    displayValue: (
                        <ActionsRow
                            templateId={template.Id}
                            onEditTemplate={onEditEventFlowTemplate}
                            onDeleteTemplate={onDeleteIncidentTemplate}
                            translations={translations}
                        />
                    ),
                    customClass: "text-center",
                    columnKey: "actionRow"
                }
            ]);

            setTemplateIncidentTableData(templateRowCell);
        }
    }, [translations, templates]);

    useEffect(() => {
        if (translations && alertTemplates && alertTemplates.length > 0) {
            setTemplateCreatorColumns(TemplateCreatorList(translations)); // Reuse same columns

            const alertRowCell: ColumnRowCell[][] = alertTemplates.map((alert, idx) => [
                {
                    id: alert.Id,
                    rowId: idx,
                    displayValue: alert.Name,
                    value: alert.Name,
                    columnKey: "name"
                },
                {
                    id: alert.Id,
                    rowId: idx,
                    displayValue: "",
                    value: "",
                    columnKey: "description"
                },
                {
                    id: alert.Id,
                    rowId: idx,
                    displayValue: (
                        <ActionsRow
                            templateId={alert.Id}
                            onEditTemplate={onEditAlertTemplate}
                            onDeleteTemplate={onDeleteAlertTemplate}
                            translations={translations}
                        />
                    ),
                    customClass: "text-center",
                    columnKey: "actionRow"
                }
            ]);

            setTemplateAlertTableData(alertRowCell);
        }
    }, [translations, alertTemplates]);

    const onDeleteIncidentTemplate = (templateId: number) => {
        deleteIncidentTemplate(templateId, {
            onSuccess: () => navigate('/templatecreator')
        });
    };

    const onDeleteAlertTemplate = (templateId: number) => {
        deleteAlertTemplate(templateId, {
            onSuccess: () => navigate('/templatecreator')
        });
    };

    const onEditEventFlowTemplate = (templateId: number) => {
        navigate(`/createeventflowtemplate?templateId=${templateId}`);
    };

    const onEditAlertTemplate = (templateId: number) => {
        navigate(`/createalerttemplate?templateId=${templateId}`);
    };

    const onClickAddIncidentTemplate = () => {
        navigate('/createeventflowtemplate');
    };

    const onClickAddAlertTemplate = () => {
        navigate('/createalerttemplate');
    };

    return (
        <ComponentWrapper active={8} subMenuActive={5}>
            {(templatesIsLoading || alertTemplatesIsLoading || deleteIncidentIsLoading || deleteAlertIsLoading) ? (
                <Spinner isGlobal />
            ) : (
                <>
                    <section className='header-btns'>
                        <div className='header-back-btn'>
                            <h2>{translations.tmpTemplates || "Templates"}</h2>
                        </div>
                    </section>
                    <section className='table-container-general Alerts'>
                        <div className='header-back-btn'>
                            <h2>{translations.alert || "Alerts"}</h2>
                            <Button buttonSize={"btn--medium"} onClick={onClickAddAlertTemplate}>
                                {translations.newalerttemplate || 'Create new alert template'} {/* Adjust translation key */}
                            </Button>
                        </div>
                        <Table columns={templateCreatorColumns} rows={templateAlertTableData} />
                    </section>
                    <section className='table-container-general Incidents'>
                        <div className='header-back-btn'>
                            <h2>{translations.Incidents || "Incidents"}</h2>
                            <Button buttonSize={"btn--medium"} onClick={onClickAddIncidentTemplate}>
                                {translations.newincidenttemplate || 'Create new incident template'}
                            </Button>
                        </div>
                        <Table columns={templateCreatorColumns} rows={templateIncidentTableData} />
                    </section>
                </>
            )}
        </ComponentWrapper>
    );
};

export default TemplateCreaterList;